/**
 * Phone Input Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableSelect, parseToVariableText } from '../../parser-utils'
import phoneInputTemplate from './phone-input.template.json'

export interface IPhoneInputComponentSettings {
  // Data settings
  'phone-input-data-value': string // Column ID
  'phone-input-data-label': VariableTextValue
  'phone-input-data-default-value': VariableSelectValue
  'phone-input-data-default-value-editable': boolean

  // Behavior settings
  'phone-input-behavior-pattern': string
  'phone-input-behavior-pattern-show-help': boolean
  'phone-input-behavior-min-char': number
  'phone-input-behavior-max-char': number
  'phone-input-behavior-required'?: boolean
  'phone-input-behavior-character-counter'?: boolean
}

export interface IPhoneInputComponent extends IBuilderComponent {
  componentId: 'phone-input'
  groupId: 'form'
  settings: IPhoneInputComponentSettings
}

/**
 * Phone Input AI Input
 */
export interface AiPhoneInputInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Phone Number"] or ["text:Enter ", "cr:contactType"]
  defaultValue?: string // Default value in select format: "text:+1234567890" or "profile:phone"
  required?: boolean // Is this field required?
}

const PHONE_INPUT_DEFAULT_TEMPLATE = phoneInputTemplate as unknown as IPhoneInputComponentSettings

export class PhoneInputComp {
  getTemplate(): IPhoneInputComponentSettings {
    return PHONE_INPUT_DEFAULT_TEMPLATE
  }

  /**
   * format phone input component settings to AI phone input input
   */
  format(settings: IPhoneInputComponentSettings): AiPhoneInputInput {
    const result: AiPhoneInputInput = {
      columnId: settings['phone-input-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['phone-input-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value
    const defaultValue = formatFromVariableSelect(settings['phone-input-data-default-value'])
    if (defaultValue) {
      result.defaultValue = defaultValue
    }

    // Required
    if (settings['phone-input-behavior-required'] !== undefined) {
      result.required = settings['phone-input-behavior-required']
    }

    return result
  }

  /**
   * parse AI phone input input to phone input component settings
   */
  parse(input: AiPhoneInputInput): IPhoneInputComponentSettings {
    const result = deepMerge(PHONE_INPUT_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['phone-input-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['phone-input-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['phone-input-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.defaultValue,
      }
    }

    return result
  }
}

